/**
 * Number formatting utilities with locale awareness.
 * Automatically detects browser locale for EU (1.234,56) vs US (1,234.56) conventions.
 */

/**
 * Detect if the current locale uses comma as decimal separator (EU convention)
 */
export function isEULocale(): boolean {
  const testNumber = 1.1;
  const formatted = testNumber.toLocaleString();
  return formatted.includes(',');
}

/**
 * Get the current locale's decimal separator
 */
export function getDecimalSeparator(): string {
  return isEULocale() ? ',' : '.';
}

/**
 * Get the current locale's thousands separator
 */
export function getThousandsSeparator(): string {
  return isEULocale() ? '.' : ',';
}

/**
 * Format a number for display with locale-appropriate thousands and decimal separators.
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string with locale-appropriate separators
 */
export function formatNumber(value: number | string | undefined | null, decimals: number = 2): string {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) {
    return '';
  }
  
  return numValue.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Format a number for display, allowing variable decimal places.
 * Shows up to maxDecimals but doesn't pad with zeros.
 * @param value - The number to format
 * @param maxDecimals - Maximum decimal places to show (default: 2)
 * @returns Formatted string
 */
export function formatNumberFlexible(value: number | string | undefined | null, maxDecimals: number = 2): string {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) {
    return '';
  }
  
  return numValue.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  });
}

/**
 * Format a number as currency with locale-appropriate formatting.
 * @param value - The number to format
 * @param currency - Currency code (EUR, USD, GBP)
 * @param decimals - Number of decimal places (default: 2, use 3 for price per kg)
 * @returns Formatted currency string
 */
export function formatCurrency(
  value: number | string | undefined | null,
  currency: string = 'EUR',
  decimals: number = 2
): string {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) {
    return '';
  }
  
  return numValue.toLocaleString(undefined, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Parse a locale-formatted number string back to a number.
 * Handles both EU (1.234,56) and US (1,234.56) formats based on current locale.
 * @param str - The formatted string to parse
 * @returns The numeric value, or NaN if parsing fails
 */
export function parseLocaleNumber(str: string | undefined | null): number {
  if (!str || str.trim() === '') {
    return NaN;
  }
  
  const cleaned = str.trim();
  
  // If the locale uses comma as decimal separator (EU)
  if (isEULocale()) {
    // Remove thousands separators (dots) and replace comma with dot
    const normalized = cleaned
      .replace(/\./g, '') // Remove thousands separators
      .replace(',', '.'); // Replace decimal comma with dot
    return parseFloat(normalized);
  } else {
    // US locale: remove thousands separators (commas)
    const normalized = cleaned.replace(/,/g, '');
    return parseFloat(normalized);
  }
}

/**
 * Format a number for input field display.
 * Unlike formatNumber, this preserves the user's input precision while typing.
 * @param value - The number to format
 * @returns Formatted string suitable for display in input
 */
export function formatForInput(value: number | string | undefined | null): string {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  
  if (typeof value === 'string') {
    // If it's already a string, just return it (user is typing)
    return value;
  }
  
  // For numbers, format with locale but don't force decimal places
  return value.toLocaleString(undefined, {
    useGrouping: true,
    maximumFractionDigits: 10,
  });
}

/**
 * Format volume in kgs for display.
 * @param kgs - Volume in kilograms
 * @param includeUnit - Whether to append "kgs" (default: true)
 * @returns Formatted volume string
 */
export function formatVolume(kgs: number | string | undefined | null, includeUnit: boolean = true): string {
  const formatted = formatNumberFlexible(kgs, 2);
  return includeUnit && formatted ? `${formatted} kgs` : formatted;
}

/**
 * Format price per kg with 3 decimal places.
 * @param price - Price value
 * @param currency - Currency code
 * @returns Formatted price string
 */
export function formatPricePerKg(price: number | string | undefined | null, currency: string = 'EUR'): string {
  if (price === undefined || price === null || price === '') {
    return '';
  }
  
  const numValue = typeof price === 'string' ? parseFloat(price) : price;
  
  if (isNaN(numValue)) {
    return '';
  }
  
  return `${formatNumber(numValue, 3)} ${currency}`;
}
